import { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import { JobStatus } from '../types/admin';

export interface OverviewKpis {
  revenueWon: number;
  activePipeline: number;
  pipelineValue: number;
  jobsThisWeek: number;
  winRate: number;
  overdueFollowUps: number;
}

export interface RevenueTrendItem {
  month: string;
  revenue: number;
}

export interface WeekScheduleItem {
  id: string;
  customerCompanyName: string;
  moveDate: string;
  crewSize: number;
  jobStatus: JobStatus;
}

export interface ActionItem {
  id: string;
  type: 'overdue_followup' | 'unassigned_job' | 'stale_quote';
  label: string;
  description: string;
  link: string;
  urgency: number;
}

export interface OverviewDashboardData {
  kpis: OverviewKpis;
  revenueTrend: RevenueTrendItem[];
  weekSchedule: WeekScheduleItem[];
  actionItems: ActionItem[];
}

interface OverviewDashboardResponse {
  success: boolean;
  data: OverviewDashboardData;
}

export function useOverviewDashboard(orgId: string | null) {
  const [data, setData] = useState<OverviewDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDashboard = useCallback(async () => {
    if (!orgId) {
      setData(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<OverviewDashboardResponse>(`/dashboard/${orgId}/overview`);
      setData(res.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load overview');
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  return { data, loading, error, refetch: fetchDashboard };
}
